/**
 * AgentConnectionForm - Compact connection form for Agent operations
 *
 * Provides cascading Azure resource selection based on agentModelType:
 * - AzureOpenAI: Subscription -> Account -> (auto-fill endpoint/key)
 * - FoundryAgentService: Subscription -> Project
 * - APIMGenAIGateway: Subscription -> APIM Instance -> API (not yet implemented)
 * - V1ChatCompletionsService: Manual endpoint + API key entry
 */

import { useCallback, useMemo, useEffect, useRef } from 'react';
import {
  makeStyles,
  tokens,
  Field,
  Input,
  Text,
  Button,
  Radio,
  RadioGroup,
} from '@coreai/fluentui-react';
import { ShieldCheckmarkRegular, PersonKey20Regular } from '@fluentui/react-icons';
import { SubscriptionDropdown } from './SubscriptionDropdown';
import { CognitiveAccountDropdown } from './CognitiveAccountDropdown';
import { ProjectDropdown } from './ProjectDropdown';
import { useSubscriptions } from '@/hooks/useAzureResources';
import type {
  AgentAuthenticationMode,
  AgentConnectionFormValues,
  AgentModelType,
} from '@/types/agentConnection';
import {
  getAgentAuthenticationMode,
  isFoundryModelType,
  normalizeArmResourceId,
  GITHUB_COPILOT_ENDPOINT,
} from '@/utils/connections';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  // Compact credentials preview — subtle card
  credentialsPreview: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    borderLeft: `3px solid ${tokens.colorBrandBackground}`,
    fontSize: tokens.fontSizeBase200,
  },
  // Foundry-specific credentials preview with purple accent
  credentialsPreviewFoundry: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    borderLeft: `3px solid ${tokens.colorPaletteLilacBorderActive}`,
    fontSize: tokens.fontSizeBase200,
  },
  credentialsHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    paddingBottom: tokens.spacingVerticalXXS,
  },
  credentialsHeaderIcon: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  credentialsHeaderLabel: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.3px',
  },
  credentialsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  credentialsLabel: {
    color: tokens.colorNeutralForeground3,
    minWidth: '56px',
    flexShrink: 0,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
  },
  credentialsValue: {
    color: tokens.colorNeutralForeground2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
  },
  authTypeSelector: {
    paddingTop: tokens.spacingVerticalXS,
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
  },
});

export interface AgentConnectionFormProps {
  /** Agent model type from operation parameters */
  agentModelType: AgentModelType;
  /** Current form values */
  values: AgentConnectionFormValues;
  /** Callback when values change */
  onChange: (values: AgentConnectionFormValues) => void;
  /** Validation errors by field */
  errors?: Record<string, string>;
  /** Whether form is disabled */
  disabled?: boolean;
  /** Hide the displayName field (when managed externally) */
  hideDisplayName?: boolean;
  /**
   * When `true` (default), the AzureOpenAI section auto-fetches the account's
   * endpoint + key from ARM as soon as an account is selected and fires the
   * result back through `onChange`. Edit surfaces should set this to `false`
   * so opening the dialog doesn't silently rewrite the persisted
   * `endpoint`/`apiKey` (which may be `@appsetting(NAME)` references we want
   * to preserve verbatim) and flip the form's dirty state on mount.
   */
  autoFillCredentials?: boolean;
  /** Trigger GitHub OAuth consent flow (GitHubCopilot type only) */
  onGitHubOAuth?: () => void;
  /** Whether GitHub OAuth is in progress */
  isOAuthInProgress?: boolean;
  /** Whether GitHub OAuth has been completed (connection authenticated) */
  isOAuthComplete?: boolean;
  /** OAuth error message to display */
  oauthError?: string | null;
  /** Whether the connection name has a validation error (blocks OAuth start) */
  hasNameError?: boolean;
}

export const AgentConnectionForm = ({
  agentModelType,
  values,
  onChange,
  errors = {},
  disabled = false,
  hideDisplayName = false,
  autoFillCredentials = true,
  onGitHubOAuth,
  isOAuthInProgress = false,
  isOAuthComplete = false,
  oauthError,
  hasNameError = false,
}: AgentConnectionFormProps) => {
  const styles = useStyles();
  const isFoundry = isFoundryModelType(agentModelType);

  // Ref to always read latest values without retriggering effects
  const valuesRef = useRef(values);
  valuesRef.current = values;

  // Pre-select the first subscription for Foundry agents so single-subscription
  // users don't have to click. Users with multiple subscriptions can still
  // change it via the SubscriptionDropdown rendered in the Foundry section.
  const { data: subscriptions } = useSubscriptions(isFoundry);
  useEffect(() => {
    if (isFoundry && subscriptions && subscriptions.length > 0 && !valuesRef.current.subscriptionId) {
      onChange({
        ...valuesRef.current,
        subscriptionId: subscriptions[0]!.subscriptionId,
      });
    }
  }, [isFoundry, subscriptions, onChange]);

  // Reset dependent fields when subscription changes
  const handleSubscriptionChange = useCallback(
    (subscriptionId: string) => {
      const newValues: AgentConnectionFormValues = {
        displayName: values.displayName,
        subscriptionId,
        ...(values.authenticationMode
          ? { authenticationMode: values.authenticationMode }
          : {}),
      };
      onChange(newValues);
    },
    [values.authenticationMode, values.displayName, onChange]
  );

  // Reset dependent fields when account changes
  const handleAccountChange = useCallback(
    (accountId: string) => {
      const newValues: AgentConnectionFormValues = {
        displayName: values.displayName,
        accountId,
        ...(values.authenticationMode
          ? { authenticationMode: values.authenticationMode }
          : {}),
      };
      if (values.subscriptionId) {
        newValues.subscriptionId = values.subscriptionId;
      }
      if (
        normalizeArmResourceId(accountId) ===
        normalizeArmResourceId(values.accountId)
      ) {
        if (values.endpoint) {
          newValues.endpoint = values.endpoint;
        }
        if (values.apiKey) {
          newValues.apiKey = values.apiKey;
        }
      }
      onChange(newValues);
    },
    [
      values.authenticationMode,
      values.accountId,
      values.apiKey,
      values.displayName,
      values.endpoint,
      values.subscriptionId,
      onChange,
    ]
  );

  // Auto-fill credentials when fetched
  const handleCredentialsFetched = useCallback(
    (endpoint: string, key?: string) => {
      onChange({
        ...values,
        endpoint,
        ...(key ? { apiKey: key } : {}),
      });
    },
    [values, onChange]
  );

  // Handle project selection — sets projectId AND derives endpoint in one update
  // to avoid stale closure issues when ProjectDropdown fires onChange + onEndpointDerived
  const handleProjectChange = useCallback(
    (projectId: string) => {
      // Derive endpoint from project ARM ID
      // Format: /subscriptions/.../accounts/{accountName}/projects/{projectName}
      const accountMatch = projectId.match(/\/accounts\/([^/]+)/i);
      const projectMatch = projectId.match(/\/projects\/([^/]+)/i);
      const endpoint = accountMatch?.[1] && projectMatch?.[1]
        ? `https://${accountMatch[1]}.services.ai.azure.com/api/projects/${projectMatch[1]}`
        : values.endpoint ?? '';

      onChange({
        ...values,
        projectId,
        ...(endpoint ? { endpoint } : {}),
      });
    },
    [values, onChange]
  );

  // Handle endpoint derived from project (no-op — handled in handleProjectChange)
  const handleProjectEndpointDerived = useCallback(
    (_endpoint: string) => { /* merged into handleProjectChange */ },
    []
  );

  // Handle manual field changes
  const handleFieldChange = useCallback(
    (field: keyof AgentConnectionFormValues, value: string) => {
      onChange({
        ...values,
        [field]: value,
      });
    },
    [values, onChange]
  );

  // Connection name placeholder based on agent type. Branch Foundry types
  // through isFoundryModelType() so V2 gets the Foundry placeholder instead of
  // falling through to the Azure OpenAI default.
  const namePlaceholder = useMemo(() => {
    if (isFoundryModelType(agentModelType)) return 'My Foundry Connection';
    switch (agentModelType) {
      case 'APIMGenAIGateway': return 'My APIM Connection';
      case 'V1ChatCompletionsService': return 'My OpenAI Connection';
      case 'GitHubCopilot': return 'My GitHub Copilot Connection';
      default: return 'My Azure OpenAI Connection';
    }
  }, [agentModelType]);
  const isAzureOpenAI = agentModelType === 'AzureOpenAI';
  const isAPIM = agentModelType === 'APIMGenAIGateway';
  const isManualEntry = agentModelType === 'V1ChatCompletionsService';
  const isGitHubCopilot = agentModelType === 'GitHubCopilot';
  const authenticationMode =
    getAgentAuthenticationMode(agentModelType, values) ?? 'apiKey';

  const handleAuthenticationModeChange = useCallback(
    (mode: AgentAuthenticationMode) => {
      if (mode === 'managedIdentity') {
        const { apiKey: _apiKey, ...valuesWithoutKey } = values;
        onChange({
          ...valuesWithoutKey,
          authenticationMode: mode,
        });
        return;
      }
      onChange({
        ...values,
        authenticationMode: mode,
      });
    },
    [onChange, values],
  );

  return (
    <div className={styles.container}>
      {/* Connection Name (hidden when managed externally via hideDisplayName) */}
      {!hideDisplayName && (
        <Field
          label="Connection Name"
          required
          validationState={errors.displayName ? 'error' : 'none'}
          validationMessage={errors.displayName || null}
          size="small"
        >
          <Input
            value={values.displayName}
            onChange={(_, data) => handleFieldChange('displayName', data.value)}
            placeholder={namePlaceholder}
            disabled={disabled}
            size="small"
            data-testid="connection-name-input"
          />
        </Field>
      )}

      {/* Azure OpenAI Flow: Subscription -> Account -> (auto-fill) */}
      {isAzureOpenAI && (
        <div className={styles.section}>
          <Field label="Authentication" required size="small">
            <RadioGroup
              className={styles.authTypeSelector}
              layout="horizontal"
              value={authenticationMode}
              onChange={(_, data) =>
                handleAuthenticationModeChange(
                  data.value as AgentAuthenticationMode,
                )
              }
              aria-label="Azure OpenAI authentication"
              data-testid="azure-openai-authentication"
            >
              <Radio value="apiKey" label="API key" disabled={disabled} />
              <Radio
                value="managedIdentity"
                label="System-assigned managed identity"
                disabled={disabled}
              />
            </RadioGroup>
          </Field>

          <SubscriptionDropdown
            value={values.subscriptionId ?? ''}
            onChange={handleSubscriptionChange}
            required
            disabled={disabled}
            {...(errors.subscriptionId && { error: errors.subscriptionId })}
          />

          <CognitiveAccountDropdown
            subscriptionId={values.subscriptionId ?? ''}
            value={values.accountId ?? ''}
            onChange={handleAccountChange}
            authenticationMode={authenticationMode}
            autoResolve={autoFillCredentials}
            {...(autoFillCredentials
              ? { onCredentialsFetched: handleCredentialsFetched }
              : {})}
            required
            disabled={disabled}
            {...(errors.accountId && { error: errors.accountId })}
          />

          {/* Credentials summary. In create mode this appears once the account
              dropdown auto-fills the endpoint/key via `onCredentialsFetched`;
              in edit mode (where auto-fill is suppressed) it appears
              immediately from the persisted seed. Same content, one gate —
              matches how the Foundry section already handles this. */}
          {values.endpoint && authenticationMode === 'apiKey' && (
            <div className={styles.credentialsPreview}>
              <div className={styles.credentialsRow}>
                <Text className={styles.credentialsLabel}>Endpoint</Text>
                <Text className={styles.credentialsValue}>{values.endpoint}</Text>
              </div>
              <div className={styles.credentialsRow}>
                <Text className={styles.credentialsLabel}>API Key</Text>
                <Text className={styles.credentialsValue}>
                  {values.apiKey ? '••••••••' : 'Not set'}
                </Text>
              </div>
            </div>
          )}
          {values.endpoint && authenticationMode === 'managedIdentity' && (
            <div className={styles.credentialsPreview}>
              <div className={styles.credentialsHeader}>
                <ShieldCheckmarkRegular className={styles.credentialsHeaderIcon} />
                <Text className={styles.credentialsHeaderLabel}>Authentication</Text>
              </div>
              <div className={styles.credentialsRow}>
                <Text className={styles.credentialsLabel}>Endpoint</Text>
                <Text className={styles.credentialsValue}>{values.endpoint}</Text>
              </div>
              <div className={styles.credentialsRow}>
                <Text className={styles.credentialsLabel}>Method</Text>
                <Text className={styles.credentialsValue}>
                  Workflow app system-assigned managed identity
                </Text>
              </div>
            </div>
          )}
        </div>
      )}

      {/* AI Foundry Flow: Subscription -> Project (subscription defaults to first available) */}
      {isFoundry && (
        <div className={styles.section}>
          <SubscriptionDropdown
            value={values.subscriptionId ?? ''}
            onChange={handleSubscriptionChange}
            required
            disabled={disabled}
            {...(errors.subscriptionId && { error: errors.subscriptionId })}
          />

          <ProjectDropdown
            subscriptionId={values.subscriptionId ?? ''}
            value={values.projectId ?? ''}
            onChange={handleProjectChange}
            onEndpointDerived={handleProjectEndpointDerived}
            required
            disabled={disabled || !values.subscriptionId}
            {...(errors.projectId && { error: errors.projectId })}
          />

          {/* Show derived endpoint + auth type */}
          {values.endpoint && (
            <div className={styles.credentialsPreviewFoundry}>
              <div className={styles.credentialsHeader}>
                <ShieldCheckmarkRegular className={styles.credentialsHeaderIcon} />
                <Text className={styles.credentialsHeaderLabel}>Credentials</Text>
              </div>
              <div className={styles.credentialsRow}>
                <Text className={styles.credentialsLabel}>Endpoint</Text>
                <Text className={styles.credentialsValue}>{values.endpoint}</Text>
              </div>
              <div className={styles.credentialsRow}>
                <Text className={styles.credentialsLabel}>Auth</Text>
                <Text className={styles.credentialsValue}>Automation environment managed identity</Text>
              </div>
            </div>
          )}
        </div>
      )}

      {/* APIM Gateway Flow */}
      {isAPIM && (
        <div className={styles.section}>
          <SubscriptionDropdown
            value={values.subscriptionId ?? ''}
            onChange={handleSubscriptionChange}
            disabled={disabled}
            {...(errors.subscriptionId && { error: errors.subscriptionId })}
          />

          <Field
            label="API Endpoint URL"
            required
            validationState={errors.endpoint ? 'error' : 'none'}
            validationMessage={errors.endpoint || null}
            hint="Full URL to your APIM-proxied OpenAI endpoint"
            size="small"
          >
            <Input
              value={values.endpoint ?? ''}
              onChange={(_, data) => handleFieldChange('endpoint', data.value)}
              placeholder="https://your-apim.azure-api.net/openai"
              disabled={disabled}
              size="small"
              data-testid="connection-endpoint-input"
            />
          </Field>

          <Field
            label="API Key"
            required
            validationState={errors.apiKey ? 'error' : 'none'}
            validationMessage={errors.apiKey || null}
            hint="Subscription key from your APIM instance"
            size="small"
          >
            <Input
              type="password"
              value={values.apiKey ?? ''}
              onChange={(_, data) => handleFieldChange('apiKey', data.value)}
              placeholder="Enter your APIM subscription key"
              disabled={disabled}
              size="small"
              data-testid="connection-api-key-input"
            />
          </Field>
        </div>
      )}

      {/* Manual Entry Flow (BYO) */}
      {isManualEntry && (
        <div className={styles.section}>
          <Field
            label="API Endpoint URL"
            required
            validationState={errors.endpoint ? 'error' : 'none'}
            validationMessage={errors.endpoint || null}
            hint="Base URL for the chat completions API"
            size="small"
          >
            <Input
              value={values.endpoint ?? ''}
              onChange={(_, data) => handleFieldChange('endpoint', data.value)}
              placeholder="https://api.openai.com/v1"
              disabled={disabled}
              size="small"
              data-testid="connection-endpoint-input"
            />
          </Field>

          <Field
            label="API Key"
            required
            validationState={errors.apiKey ? 'error' : 'none'}
            validationMessage={errors.apiKey || null}
            size="small"
          >
            <Input
              type="password"
              value={values.apiKey ?? ''}
              onChange={(_, data) => handleFieldChange('apiKey', data.value)}
              placeholder="sk-..."
              disabled={disabled}
              size="small"
              data-testid="connection-api-key-input"
            />
          </Field>
        </div>
      )}

      {/* GitHub Copilot Flow: OAuth consent via ottogithub connector */}
      {isGitHubCopilot && (
        <div className={styles.section}>
          {isOAuthComplete ? (
            <div className={styles.credentialsPreview}>
              <div className={styles.credentialsHeader}>
                <ShieldCheckmarkRegular className={styles.credentialsHeaderIcon} />
                <Text className={styles.credentialsHeaderLabel}>Authenticated</Text>
              </div>
              <div className={styles.credentialsRow}>
                <Text className={styles.credentialsLabel}>Provider</Text>
                <Text className={styles.credentialsValue}>GitHub Copilot</Text>
              </div>
              <div className={styles.credentialsRow}>
                <Text className={styles.credentialsLabel}>Endpoint</Text>
                <Text className={styles.credentialsValue}>{GITHUB_COPILOT_ENDPOINT}</Text>
              </div>
            </div>
          ) : (
            <>
              <Button
                appearance="primary"
                icon={<PersonKey20Regular />}
                onClick={onGitHubOAuth}
                disabled={disabled || isOAuthInProgress || !values.displayName || hasNameError || !onGitHubOAuth}
                data-testid="github-oauth-button"
              >
                {isOAuthInProgress ? 'Signing in…' : 'Sign in with GitHub'}
              </Button>
              {oauthError && (
                <Text role="alert" className={styles.errorText} data-testid="github-oauth-error">
                  {oauthError}
                </Text>
              )}
              {!oauthError && errors.oauth && (
                <Text role="alert" className={styles.errorText} data-testid="github-oauth-validation-error">
                  {errors.oauth}
                </Text>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

AgentConnectionForm.displayName = 'AgentConnectionForm';
